import { customElement, property } from "lit/decorators.js";
import iframeResize from "@iframe-resizer/parent";
import { DodonaElement } from "@/components/meta/dodona_element";
import { showLightbox } from "@/lightbox";
import { triggerKaTeX } from "@/utilities";
import { scrollPaddingTop } from "@/scroll_pin";
import { keepPlaceAfterLayout, LAYOUT_SNAPSHOT_EVENT, type LayoutSnapshot } from "@/motion";
import type { DescriptionHeading, FrameMessage, ParentMessage } from "@/iframe_messages";

type ResizedIframe = HTMLIFrameElement & {
    iframeResizer?: { sendMessage(message: ParentMessage): void; moveToAnchor(anchor: string): void };
};

function renderSlideMath(): void {
    document.querySelectorAll<HTMLElement>(".gslide-description").forEach(triggerKaTeX);
}

const HEADING_HASH = "#description/";

/**
 * The address of a heading in the description. It has a namespace of its own, so a heading never
 * shares its address with an element of the page, whatever its id.
 */
export function headingHash(id: string): string {
    return `${HEADING_HASH}${encodeURIComponent(id)}`;
}

// The id of the description heading this address names, or null for an address on the page or a malformed one.
export function headingInHash(hash: string = location.hash): string | null {
    if (!hash.startsWith(HEADING_HASH)) return null;
    try {
        return decodeURIComponent(hash.slice(HEADING_HASH.length));
    } catch {
        return null;
    }
}

/**
 * A place in a description, as far into a section as a fraction of its height. A section runs from
 * a heading (or the top of the description) to the next heading (or the bottom). The page only sees
 * the headings of a description in an iframe, so text that rewraps at another width is followed
 * section by section.
 */
type ReadingAnchor = { from: string | null; to: string | null; fraction: number };

type HeadingTop = Pick<DescriptionHeading, "id" | "top">;

// The edges of the sections: the top, each heading, the bottom.
function edges(headings: HeadingTop[], height: number): { id: string | null; top: number }[] {
    const sorted = [...headings].sort((a, b) => a.top - b.top);
    return [{ id: null, top: 0 }, ...sorted, { id: null, top: height }];
}

/** The anchor of the point `y` px below the top of a description of `height` px. */
export function readingAnchor(headings: HeadingTop[], height: number, y: number): ReadingAnchor {
    const points = edges(headings, height);
    let index = 0;
    while (index < points.length - 2 && points[index + 1].top <= y) index++;
    const start = points[index];
    const end = points[index + 1];
    const span = end.top - start.top;
    const fraction = span > 0 ? Math.min(1, Math.max(0, (y - start.top) / span)) : 0;
    return { from: start.id, to: end.id, fraction };
}

/**
 * Where the anchor is in the description now, in px below its top. Null when a heading of the
 * anchor is gone.
 */
export function anchorOffset(anchor: ReadingAnchor, headings: HeadingTop[], height: number): number | null {
    const top = (id: string | null, fallback: number): number | null =>
        id === null ? fallback : (headings.find((heading) => heading.id === id)?.top ?? null);
    const start = top(anchor.from, 0);
    const end = top(anchor.to, height);
    if (start === null || end === null) return null;
    return start + anchor.fraction * (end - start);
}

/**
 * If the page is loaded with a hash, the browser scrolls to that element before the iframe has
 * its height, which can move the element away. Scroll to it again after the first resize.
 */
function afterResize(details: { type: string }): void {
    if (details.type === "init" && location.hash.length > 1 && headingInHash() === null) {
        const hash = location.hash;
        location.hash = "#"; // some browsers only scroll after a change
        location.hash = hash;
    }
}

// Scrolls to an anchor in the description with the gap scroll-padding-top leaves below the navbar on the page.
function onFrameScroll(position: { x: number; y: number }): boolean {
    window.scrollTo(position.x, position.y - scrollPaddingTop());
    return false;
}

/**
 * Wraps the sandboxed iframe that shows an activity description. It sizes the iframe to its
 * content and shows the lightbox the iframe asks for.
 * It forwards the answers of a composite exercise as bubbling events, which d-handin-card handles.
 *
 * With `headings`, it asks the iframe for the headings of the description and dispatches them in a
 * bubbling `description-headings` event, again whenever they move. A heading has the address
 * `headingHash(id)`; the page scrolls to the heading such an address names when the headings first
 * arrive, because HTML descriptions only get their ids then, and whenever the hash changes, as on
 * Back and Forward. navigateToHeading goes to a heading the same way and adds it to the history.
 * An address naming an id the description did not report goes nowhere: iframe-resizer would look
 * the id up on the page instead, and scroll to the element of the page with that id.
 *
 * With `headings`, a change of the page layout (transitionLayout) keeps the place the user reads at
 * the top of the window, below the navbar: the description gets another width and height as a
 * column next to it opens or closes, and its text and images move. The place is kept for a second,
 * while the iframe reports its new size and headings, unless the page scrolled otherwise during or
 * after the change.
 *
 * Renders no template of its own.
 *
 * @element d-description-frame
 *
 * @prop {boolean} headings - Whether the page lists the headings of the description.
 * @fires composite-register - With `detail.id`, for each question of a composite exercise.
 * @fires composite-answer - With `detail.id` and `detail.value`, when the answer to a question changes.
 * @fires description-headings - With `detail.headings`, the section headings of the description, and
 *   `detail.intro`, whether it shows something before the first of them.
 */
@customElement("d-description-frame")
export class DescriptionFrame extends DodonaElement {
    @property({ type: Boolean })
    headings = false;

    private iframe: ResizedIframe;
    // The headings the description last reported, or null until it does.
    private reportedHeadings: DescriptionHeading[] | null = null;
    // The height of the iframe the last time it was shown.
    private shownHeight = "";
    // The place to keep across a change of the layout, while it is kept.
    private readingAnchor: ReadingAnchor | null = null;
    // Where the page was scrolled before the change, then after it and after each correction.
    private anchoredScroll = 0;
    private anchorTimer: ReturnType<typeof setTimeout> | null = null;
    // The width of the description in the old snapshot of a layout transition.
    private widthBefore = 0;

    connectedCallback(): void {
        super.connectedCallback();
        this.iframe = this.querySelector("iframe");
        window.addEventListener("message", this.onWindowMessage);
        if (this.headings) {
            window.addEventListener("hashchange", this.moveToHash);
            document.addEventListener(LAYOUT_SNAPSHOT_EVENT, this.onLayoutSnapshot);
        }
        // The library keeps its state on the iframe, so moving this element must not set it up again.
        if (!this.iframe.iframeResizer) {
            iframeResize(
                {
                    license: "1jy4dww5qzv-s54r73oxcn-v59f4kfgfz",
                    onResized: this.onFrameResized,
                    onMessage: this.onFrameMessage,
                    onReady: this.onFrameReady,
                    onScroll: onFrameScroll,
                    inPageLinks: true,
                    // Until a lazy iframe loads the description, it holds a blank page of this origin, which refuses messages for the sandbox.
                    waitForLoad: this.iframe.contentDocument?.URL === "about:blank",
                },
                this.iframe,
            );
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        window.removeEventListener("message", this.onWindowMessage);
        window.removeEventListener("hashchange", this.moveToHash);
        document.removeEventListener(LAYOUT_SNAPSHOT_EVENT, this.onLayoutSnapshot);
    }

    // The point of the description at the top of the window, below the navbar, in px below its top.
    private readingLine(): { y: number; height: number } {
        const box = this.iframe.getBoundingClientRect();
        return { y: scrollPaddingTop() - box.top, height: box.height };
    }

    /**
     * Before a change of the layout, takes the place at the top of the window when it is in the
     * description; after it, starts keeping that place, unless the change scrolled the page itself.
     * The column of the description is marked when the description is shown before and after the
     * change at another width (`data-transition-reflows`), so its old and new snapshot crossfade
     * (activities.css.scss): folding it into its tab or unfolding it is no reflow.
     */
    private onLayoutSnapshot = (event: Event): void => {
        const snapshot = (event as CustomEvent<LayoutSnapshot>).detail;
        const width = this.iframe.getBoundingClientRect().width;
        const column = this.closest<HTMLElement>(".activity-page-main");
        if (snapshot === "old") {
            this.widthBefore = width;
            column?.removeAttribute("data-transition-reflows");
        } else {
            column?.toggleAttribute(
                "data-transition-reflows",
                this.widthBefore > 0 && width > 0 && width !== this.widthBefore,
            );
        }

        if (snapshot === "old") {
            this.stopKeepingPlace();
            const { y, height } = this.readingLine();
            if (height === 0 || y <= 0 || y >= height) return;
            this.readingAnchor = readingAnchor(this.reportedHeadings ?? [], height, y);
            this.anchoredScroll = window.scrollY;
        } else {
            this.keepPlace();
            if (!this.readingAnchor) return;
            keepPlaceAfterLayout(this.stopKeepingPlace);
            this.anchorTimer = setTimeout(this.stopKeepingPlace, 1000);
        }
    };

    // Scrolls the page so the anchored place is at the top of the window again.
    private keepPlace(): void {
        const anchor = this.readingAnchor;
        if (!anchor) return;
        if (window.scrollY !== this.anchoredScroll) {
            this.stopKeepingPlace();
            return;
        }
        const { y, height } = this.readingLine();
        // A folded description has no place to keep: the page goes back where it was when it unfolds.
        const offset = height === 0 ? null : anchorOffset(anchor, this.reportedHeadings ?? [], height);
        if (offset === null) {
            this.stopKeepingPlace();
            return;
        }
        window.scrollBy({ top: Math.round(offset - y), behavior: "instant" });
        this.anchoredScroll = window.scrollY;
    }

    private stopKeepingPlace = (): void => {
        this.readingAnchor = null;
        if (this.anchorTimer !== null) clearTimeout(this.anchorTimer);
        this.anchorTimer = null;
    };

    // A hidden iframe, as in the folded description column, reports the height of its empty
    // viewport. It keeps the height it had instead, so the page is as long as before as soon as it
    // shows again, and can scroll back to where it was.
    private onFrameResized = (details: { type: string }): void => {
        if (this.iframe.getClientRects().length > 0) {
            this.shownHeight = this.iframe.style.height;
        } else if (this.shownHeight) {
            this.iframe.style.height = this.shownHeight;
        }
        afterResize(details);
        this.keepPlace();
    };

    // iframe-resizer has connected to the iframe, also after it loaded again, so it can take requests.
    private onFrameReady = (): void => {
        if (this.headings) this.iframe.iframeResizer.sendMessage({ type: "send-headings" });
    };

    private onFrameMessage = ({ message }: { message: FrameMessage }): void => {
        if (message.type === "lightbox") {
            void showLightbox(message.content, renderSlideMath);
        } else if (message.type === "headings") {
            const first = this.reportedHeadings === null;
            this.reportedHeadings = message.headings;
            this.keepPlace();
            this.forward("description-headings", { headings: message.headings, intro: message.intro });
            if (first) this.moveToHash();
        }
    };

    private moveToHash = (): void => {
        const id = headingInHash();
        if (id) this.moveToHeading(id);
    };

    // Puts the address of the heading with this id in the history, unless it is there already, and scrolls to it.
    navigateToHeading(id: string): void {
        const hash = headingHash(id);
        if (location.hash !== hash) history.pushState(null, "", hash);
        this.moveToHeading(id);
    }

    // iframe-resizer decodes the anchor in the frame and cuts it at its first #, so the id goes encoded.
    private moveToHeading(id: string): void {
        if (!this.reportedHeadings?.some((heading) => heading.id === id)) return;
        this.iframe.iframeResizer?.moveToAnchor(`#${encodeURIComponent(id)}`);
    }

    // Moves the keyboard focus to the heading with this id in the description.
    focusHeading(id: string): void {
        this.iframe.iframeResizer?.sendMessage({ type: "focus-heading", id });
    }

    // The composite questions post directly, so only trust messages from this
    // iframe's own window: third-party content embedded in the description posts from its own window.
    // The origin must match too, because a page the iframe navigates to posts from the same window.
    private onWindowMessage = (event: MessageEvent<FrameMessage>): void => {
        if (event.source !== this.iframe.contentWindow || event.origin !== new URL(this.iframe.src).origin) return;
        const message = event.data;
        if (message?.type === "dodona-composite-register") {
            this.forward("composite-register", { id: message.id });
        } else if (message?.type === "dodona-composite-answer") {
            this.forward("composite-answer", { id: message.id, value: message.value });
        }
    };

    private forward(type: string, detail: object): void {
        this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true }));
    }
}
